import { useEffect, useRef, useState } from 'react';
import type { CategorySuggestion } from '@/types';
import { categoryBudgetApi } from '@/lib/api';
import { useAuthedFetcher } from '@/hooks/queries/swrConfig';

export interface UseCategorySuggestionArgs {
  /** Gate: open create-mode form, not mid-save. False clears any pending/inflight work. */
  enabled: boolean;
  /** The category name as typed; suggestions fire after it settles. */
  name: string;
  /** Preset hexes resolved from the theme (useChartSeries) — sent so the model picks by index. */
  palette: readonly string[];
  onSuggestion: (suggestion: CategorySuggestion) => void;
}

const DEBOUNCE_MS = 600;
const MIN_NAME_LENGTH = 3;

/**
 * Debounced "title → icon/color/kind" suggestion call. Failure of any kind is a
 * silent no-op (the endpoint answers 204 when it has nothing) — the form just
 * behaves as if the feature didn't exist.
 *
 * Not an SWR hook on purpose: this is an imperative fire-and-apply call keyed to
 * typing, where revalidation semantics would only add surprise re-fires.
 */
export function useCategorySuggestion({
  enabled,
  name,
  palette,
  onSuggestion,
}: UseCategorySuggestionArgs): { loading: boolean } {
  const authedFetch = useAuthedFetcher();
  const [loading, setLoading] = useState(false);

  // Refs so the main effect re-runs only when typing/gating changes, not on
  // every parent render that recreates the callback or the palette array.
  // Updated in an effect (not during render); it is declared first, so it
  // commits before the main effect reads the refs.
  const onSuggestionRef = useRef(onSuggestion);
  const paletteRef = useRef(palette);
  useEffect(() => {
    onSuggestionRef.current = onSuggestion;
    paletteRef.current = palette;
  });
  const controllerRef = useRef<AbortController | null>(null);

  const trimmed = name.trim();

  useEffect(() => {
    if (!enabled || trimmed.length < MIN_NAME_LENGTH) {
      controllerRef.current?.abort();
      controllerRef.current = null;
      setLoading(false);
      return;
    }

    const timer = setTimeout(() => {
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      setLoading(true);

      authedFetch((token) =>
        categoryBudgetApi.suggestCategory(
          token,
          { name: trimmed, palette: [...paletteRef.current] },
          controller.signal,
        ),
      )
        .then((suggestion) => {
          // A response that lost the race to a newer request (or to close) is stale.
          if (controller.signal.aborted || !suggestion) return;
          onSuggestionRef.current(suggestion);
        })
        .catch(() => {
          // Silent by contract: abort, network, auth — the form works without AI.
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      controllerRef.current?.abort();
      controllerRef.current = null;
      setLoading(false);
    };
  }, [enabled, trimmed, authedFetch]);

  return { loading };
}
